"use dom";

import TooltipWrapper from "@memorang/ui/src/components/TooltipWrapper";
import type { IconType } from "@memorang/ui/src/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "react-native-paper";
import { useTTSContext } from "./TTSContext";
import { getTTSCacheKey, getTtsAudioData } from "./utils";

type Props = {
	itemId: string;
	explanation?: string;
	playIcon?: IconType;
	mode?: React.ComponentProps<typeof IconButton>["mode"];
	size?: number;
	disabled?: boolean;
	allowPrefetch?: boolean;
	eventName?: string;
	trackCustomEvent?: (
		params: Record<string, string | number | undefined>,
	) => void;
	iconColor?: string;
};

type PlaybackStatus = "stopped" | "playing" | "paused" | "loading" | "unloaded";

const TTSContainer = ({
	explanation: initialExplanation,
	playIcon = "headphones",
	mode,
	size = 24,
	disabled = false,
	allowPrefetch: _allowPrefetch = false, // not required as web & android support true streaming
	itemId,
	trackCustomEvent,
	eventName,
	iconColor,
}: Props) => {
	const [playbackStatus, setPlaybackStatus] =
		useState<PlaybackStatus>("stopped");
	const [isPlaying, setIsPlaying] = useState(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const mediaSourceRef = useRef<MediaSource | null>(null);
	const { t } = useTranslation();
	const { voiceId, tenantId, org, appId, appName } = useTTSContext();

	const handleError = useCallback((message: string) => {
		console.error(message);
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.src = "";
		}
		if (mediaSourceRef.current) {
			URL.revokeObjectURL(audioRef.current?.src || "");
		}
	}, []);

	const handleTracking = useCallback(
		(status: PlaybackStatus) => {
			if (trackCustomEvent && eventName) {
				trackCustomEvent({
					eventName,
					itemId,
					status,
				});
			}
		},
		[trackCustomEvent, eventName, itemId],
	);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const startStreaming = useCallback(async () => {
		if (!initialExplanation?.trim()) {
			handleError("Text cannot be empty");
			return;
		}

		if (audioRef?.current?.src) {
			audioRef.current.play();
			setPlaybackStatus("playing");
			handleTracking("playing");
			return;
		}

		setPlaybackStatus("loading");

		try {
			const audio = new Audio();
			audioRef.current = audio;

			audio.addEventListener("play", () => {
				setIsPlaying(true);
				setPlaybackStatus("playing");
				handleTracking("playing");
			});

			audio.addEventListener("pause", () => {
				setIsPlaying(false);
				setPlaybackStatus("paused");
				handleTracking("paused");
			});

			audio.addEventListener("ended", () => {
				setIsPlaying(false);
				setPlaybackStatus("stopped");
				handleTracking("stopped");
			});

			const ttsKey = await getTTSCacheKey(
				org ?? "",
				tenantId ?? "",
				appId ?? "",
				initialExplanation,
			);

			const audioData = await getTtsAudioData(
				initialExplanation,
				voiceId ?? "",
				appName ?? "",
				ttsKey,
				true,
			);

			// If we got a cached URL, use it directly
			if (typeof audioData === "string") {
				audio.src = audioData;
				await audio.play();
				setPlaybackStatus("playing");
				handleTracking("playing");
				return;
			}

			// Handle streaming response
			const mediaSource = new MediaSource();
			mediaSourceRef.current = mediaSource;

			audio.src = URL.createObjectURL(mediaSource);

			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
			mediaSource.addEventListener("sourceopen", async () => {
				try {
					const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
					const reader = audioData.getReader();
					const bufferQueue: Uint8Array[] = [];
					let isProcessing = false;

					// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
					const processQueue = async () => {
						if (
							isProcessing ||
							bufferQueue.length === 0 ||
							sourceBuffer.updating
						) {
							return;
						}

						isProcessing = true;
						try {
							const chunk = bufferQueue.shift();
							if (chunk) {
								//@ts-ignore - TODO: fix this
								sourceBuffer.appendBuffer(chunk);
								await new Promise<void>((resolve) => {
									sourceBuffer.addEventListener(
										"updateend",
										() => {
											isProcessing = false;
											resolve();
											processQueue();
										},
										{ once: true },
									);
								});
							}
						} catch (err) {
							isProcessing = false;
							handleError(
								`Buffer error: ${err instanceof Error ? err.message : String(err)}`,
							);
						}
					};

					while (true) {
						const { done, value } = await reader.read();
						if (done) {
							break;
						}

						bufferQueue.push(value);
						if (!isProcessing) {
							await processQueue();
						}
					}

					while (bufferQueue.length > 0 || sourceBuffer.updating) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
					mediaSource.endOfStream();
				} catch (err) {
					handleError(
						`Failed to process audio stream: ${err instanceof Error ? err.message : String(err)}`,
					);
				}
			});

			await audio.play();
			setPlaybackStatus("playing");
		} catch (err) {
			handleError(
				`Failed to generate audio: ${err instanceof Error ? err.message : "Unknown error"}`,
			);
		}
	}, [
		initialExplanation,
		handleError,
		org,
		tenantId,
		appId,
		voiceId,
		appName,
		handleTracking,
	]);

	const togglePlayback = useCallback(() => {
		if (!audioRef.current) {
			return;
		}
		if (isPlaying) {
			audioRef.current.pause();
			setPlaybackStatus("paused");
		} else {
			audioRef.current.play().catch((err) => {
				handleError(`Playback failed: ${err.message}`);
			});
			setPlaybackStatus("playing");
		}
	}, [isPlaying, handleError]);

	useEffect(() => {
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.src = "";
			}
			if (mediaSourceRef.current) {
				URL.revokeObjectURL(audioRef.current?.src || "");
			}
		};
	}, []);

	const handlePlaybackControl = useCallback(() => {
		if (playbackStatus === "loading") {
			return;
		}
		if (playbackStatus === "stopped") {
			startStreaming();
		} else {
			togglePlayback();
		}
	}, [playbackStatus, startStreaming, togglePlayback]);

	const getPlaybackIcon = () => {
		switch (playbackStatus) {
			case "stopped":
			case "unloaded":
			case "paused":
				return playIcon;
			case "playing":
				return "pause";
			default:
				return "play";
		}
	};

	return (
		<TooltipWrapper title={t("tts.tooltip")}>
			<IconButton
				icon={getPlaybackIcon()}
				size={size}
				mode={mode}
				onPress={handlePlaybackControl}
				disabled={playbackStatus === "loading" || disabled}
				loading={playbackStatus === "loading"}
				style={{ margin: 0 }}
				iconColor={iconColor}
			/>
		</TooltipWrapper>
	);
};

export default TTSContainer;
